// Download button for binary exports: idle, busy ("Creating…"), done (file name) and error states; saves the blob under the server file name.
import { useMutation } from "@tanstack/react-query";
import { CircleCheckIcon, DownloadIcon, type LucideIcon } from "lucide-react";

import { ProblemState } from "@/components/common/ProblemState";
import { Button, type ButtonProps } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { downloadFile } from "@/lib/download";

interface DownloadButtonProps extends Omit<ButtonProps, "onClick"> {
  path: string;
  fallbackName: string;
  label: string;
  icon?: LucideIcon;
  method?: "GET" | "POST";
  body?: unknown;
}

export function DownloadButton({
  path,
  fallbackName,
  label,
  icon: Icon = DownloadIcon,
  method = "GET",
  body,
  disabled,
  ...props
}: DownloadButtonProps) {
  const mutation = useMutation({
    mutationFn: () =>
      downloadFile(path, fallbackName, {
        method,
        ...(body === undefined
          ? {}
          : { body: JSON.stringify(body), headers: { "Content-Type": "application/json" } }),
      }),
  });
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          disabled={disabled === true || mutation.isPending}
          onClick={() => {
            mutation.mutate();
          }}
          {...props}
        >
          {mutation.isPending ? <Spinner label="Creating file" /> : <Icon aria-hidden />}
          {mutation.isPending ? "Creating…" : label}
        </Button>
        {mutation.isSuccess ? (
          <span
            role="status"
            className="inline-flex items-center gap-2 text-chip font-semibold text-brand-blue"
          >
            <CircleCheckIcon aria-hidden className="size-4" />
            {mutation.data}
          </span>
        ) : null}
      </div>
      {mutation.isError ? (
        <ProblemState
          problem={mutation.error}
          compact
          retry={() => {
            mutation.mutate();
          }}
        />
      ) : null}
    </div>
  );
}
